import React, { useState, useEffect } from 'react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import PageHeader from './components/PageHeader';
import ViewToolbar from './components/ViewToolbar';
import FilterPanel from './components/FilterPanel';
import ListView from './components/ListView';
import KanbanView from './components/KanbanView';
import CalendarView from './components/CalendarView';
import TimelineView from './components/TimelineView';
import BulkActionsBar from './components/BulkActionsBar';
import TaskDrawer from './components/TaskDrawer';
import useTaskBoardController from './hooks/useTaskBoardController';

const TaskBoardPage = () => {
  const {
    tasks,
    currentView,
    setCurrentView,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    selectedTasks,
    toggleTaskSelection,
    selectAllTasks,
    clearSelection,
    isFilterPanelOpen,
    setIsFilterPanelOpen,
    filters,
    setFilters,
    savedViews,
    saveView,
    loadView,
    setDefaultView,
    deleteView,
    density,
    setDensity,
    bulkUpdateTasks,
    updateTask,
    canBulkEdit,
    undoStack,
    undoLastBulkAction,
    allTasks,
  } = useTaskBoardController();

  // Add missing state declarations
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState('all');

  // Mock projects data
  const projects = [
    { value: 'all', label: 'All Projects' },
    { value: 'proj1', label: 'HR System Modernization' },
    { value: 'proj2', label: 'Employee Wellness Program' },
    { value: 'proj3', label: 'Office Expansion Dubai' },
    { value: 'proj4', label: 'Digital Marketing Campaign' },
  ];

  // Mock helper functions - Add these declarations
  const getRiskLevel = (task) => {
    // Basic risk calculation logic
    return 'low';
  };

  const isOverdue = (task) => {
    if (!task?.dueDate) return false;
    return new Date(task.dueDate) < new Date();
  };

  const calculateTaskAging = (task) => {
    if (!task?.createdAt) return 0;
    const created = new Date(task.createdAt);
    const now = new Date();
    return Math.floor((now - created) / (1000 * 60 * 60 * 24));
  };

  const calculateBlockedDuration = (task) => {
    if (!task?.blockedSince) return 0;
    const blocked = new Date(task.blockedSince);
    const now = new Date();
    return Math.floor((now - blocked) / (1000 * 60 * 60 * 24));
  };

  const canEditTask = (task) => {
    return true; // Simplified permission check
  };

  const isTaskBlocked = (task) => {
    return task?.status === 'blocked' || task?.blocked === true;
  };

  const getBlockReason = (task) => {
    return task?.blockReason || 'No reason provided';
  };

  const statusConfig = {
    'todo': { label: 'To Do', color: 'gray' },
    'in-progress': { label: 'In Progress', color: 'blue' },
    'review': { label: 'Review', color: 'yellow' },
    'blocked': { label: 'Blocked', color: 'red' },
    'done': { label: 'Done', color: 'green' }
  };

  const canTransition = (task, fromStatus, toStatus) => {
    // Simplified transition validation
    return true;
  };

  const transitionTaskStatus = (taskId, fromStatus, toStatus) => {
    const task = allTasks?.find(t => t.id === taskId);
    if (!task) {
      return { success: false, message: 'Task not found' };
    }

    if (!canTransition(task, fromStatus, toStatus)) {
      return { success: false, message: 'Invalid status transition' };
    }

    updateTask(taskId, { status: toStatus });
    return { success: true };
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl/Cmd + K: Open task drawer
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'k') {
        e?.preventDefault();
        setIsTaskDrawerOpen(true);
        setSelectedTask(null);
      }
      // Escape: Close drawer or clear selection
      if (e?.key === 'Escape') {
        if (isTaskDrawerOpen) {
          setIsTaskDrawerOpen(false);
        } else if (selectedTasks?.length > 0) {
          clearSelection();
        }
      }
      // Ctrl/Cmd + A: Select all (when not in input)
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'a' && !['INPUT', 'TEXTAREA']?.includes(e?.target?.tagName)) {
        e?.preventDefault();
        selectAllTasks();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isTaskDrawerOpen, selectedTasks]);

  // Handlers
  const handleNewTask = () => {
    setSelectedTask(null);
    setIsTaskDrawerOpen(true);
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setIsTaskDrawerOpen(true);
  };

  const handleTaskSave = (taskData) => {
    if (selectedTask) {
      updateTask(selectedTask?.id, taskData);
    } else {
      // For new tasks, we'd add them (simplified here)
      updateTask(taskData?.id, taskData);
    }
  };

  const handleDragEnd = (result) => {
    if (!result?.destination) return;

    const taskId = result?.draggableId;
    const fromStatus = result?.source?.droppableId;
    const toStatus = result?.destination?.droppableId;

    if (fromStatus === toStatus) return;

    const transitionResult = transitionTaskStatus(taskId, fromStatus, toStatus);
    if (!transitionResult?.success) {
      alert(transitionResult?.message);
    }
  };

  const handleBulkAction = (action, payload) => {
    const result = bulkUpdateTasks(selectedTasks, action, payload);
    if (result?.success) {
      clearSelection();
    } else {
      alert(result?.message);
    }
  };

  const handleSelectAll = () => {
    if (selectedTasks?.length === tasks?.length) {
      clearSelection();
    } else {
      selectAllTasks();
    }
  };

  const selectedTasksData = allTasks?.filter(t => selectedTasks?.includes(t?.id));
  const canBulkEditSelected = canBulkEdit(selectedTasksData);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 pt-6">
        <ComingSoonBanner
          icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.251 2.251 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" /></svg>}
          title="Task Board"
          description="Task Board is currently in development. This is a preview of the upcoming feature."
          gradientFrom="from-indigo-500"
          gradientTo="to-blue-500"
        />
      </div>
      {/* Page Header */}
      <PageHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedProject={selectedProject}
        onProjectChange={setSelectedProject}
        projects={projects}
        onNewTask={handleNewTask}
      />

      {/* View Toolbar */}
      <ViewToolbar
        currentView={currentView}
        onViewChange={setCurrentView}
        sortBy={sortBy}
        onSortChange={setSortBy}
        sortDirection={sortDirection}
        onSortDirectionChange={setSortDirection}
        savedViews={savedViews}
        onSaveView={saveView}
        onLoadView={loadView}
        onSetDefaultView={setDefaultView}
        onDeleteView={deleteView}
        density={density}
        onDensityChange={setDensity}
        filters={filters}
      />

      {/* Main Content Area */}
      <div className="flex">
        {/* Filter Panel */}
        <FilterPanel
          isOpen={isFilterPanelOpen}
          onToggle={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
          filters={filters}
          onFiltersChange={setFilters}
          onClearFilters={() => setFilters({
            search: '',
            projectId: 'all',
            status: [],
            assigneeIds: [],
            priorities: [],
            tags: [],
            dateRange: { start: '', end: '' },
            taskType: '',
            hasDependencies: ''
          })}
          tasks={allTasks}
        />

        {/* Task View Area */}
        <div className={`flex-1 p-6 transition-all duration-300 ${
          isFilterPanelOpen ? 'ml-0' : 'ml-0'
        }`}>
          {currentView === 'list' && (
            <ListView
              tasks={tasks}
              selectedTasks={selectedTasks}
              onTaskSelect={toggleTaskSelection}
              onSelectAll={handleSelectAll}
              onTaskClick={handleTaskClick}
              onTaskUpdate={updateTask}
              density={density}
              getRiskLevel={getRiskLevel}
              isOverdue={isOverdue}
              calculateTaskAging={calculateTaskAging}
            />
          )}

          {currentView === 'kanban' && (
            <KanbanView
              tasks={tasks}
              allTasks={allTasks}
              statusConfig={statusConfig}
              onTaskClick={handleTaskClick}
              onDragEnd={handleDragEnd}
              canTransition={canTransition}
              getRiskLevel={getRiskLevel}
              isOverdue={isOverdue}
              calculateBlockedDuration={calculateBlockedDuration}
            />
          )}

          {currentView === 'calendar' && (
            <CalendarView
              tasks={tasks}
              onTaskClick={handleTaskClick}
            />
          )}

          {currentView === 'timeline' && (
            <TimelineView
              tasks={tasks}
              onTaskClick={handleTaskClick}
            />
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedTasks?.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedTasks?.length}
          onAction={handleBulkAction}
          onCancel={clearSelection}
          canBulkEdit={canBulkEditSelected}
          selectedTasks={selectedTasksData}
          onUndo={undoLastBulkAction}
          canUndo={undoStack?.length > 0}
          lastAction={undoStack?.[undoStack?.length - 1]?.action}
        />
      )}

      {/* Task Drawer */}
      <TaskDrawer
        isOpen={isTaskDrawerOpen}
        onClose={() => setIsTaskDrawerOpen(false)}
        task={selectedTask}
        onSave={handleTaskSave}
        canEdit={canEditTask}
        allTasks={allTasks}
        getRiskLevel={getRiskLevel}
        calculateTaskAging={calculateTaskAging}
        isOverdue={isOverdue}
        canTransition={canTransition}
        statusConfig={statusConfig}
        isTaskBlocked={isTaskBlocked}
        getBlockReason={getBlockReason}
      />

      {/* Keyboard Shortcuts Helper (Optional) */}
      <div className="fixed bottom-4 right-4 bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg opacity-0 hover:opacity-100 transition-opacity">
        <div className="space-y-1">
          <div><kbd className="bg-gray-700 px-1 rounded">Ctrl+K</kbd> New Task</div>
          <div><kbd className="bg-gray-700 px-1 rounded">Esc</kbd> Close/Clear</div>
          <div><kbd className="bg-gray-700 px-1 rounded">Ctrl+A</kbd> Select All</div>
        </div>
      </div>
    </div>
  );
};

export default TaskBoardPage;
